<template>
    <div class="row gx-3 align-items-baseline">
        <pc-label
            v-bind:label="label"
            v-bind:for-id="showPcFile ? inputId : null"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            class="col-auto"
            v-on:help-clicked="emitHelpClicked">
            <pc-sensitive-icon
                v-if="isSensitive"
                attachment />
        </pc-label>
        <div
            v-if="isOptionalChoiceMade"
            class="col-auto">
            <span v-if="signatureChoiceIsElectronic">
                {{ electronicSignatureChosenLabel }}
            </span>
            <span v-if="signatureChoiceIsSignAndUpload">
                {{ signAndUploadChosenLabel }}
            </span>
            <pc-button
                v-if="showChange"
                class="btn-sm mb-1 ms-2"
                style="min-width: 0"
                v-bind:disabled="readonly"
                v-on:click="changeOptionalChoice">
                {{ changeSignatureTypeButtonLabel }}
            </pc-button>
        </div>
        <div class="col-12">
            <slot name="help" />
        </div>
        <div
            v-if="showError"
            class="fw-bold text-danger col-12">
            {{ error }}
        </div>
        <template v-else>
            <transition
                v-bind:css="false"
                mode="out-in"
                v-on:enter="enter"
                v-on:leave="leave">
                <div
                    v-if="showAsk"
                    key="ask"
                    class="col-12">
                    <div class="row gx-3 align-items-baseline">
                        <div class="col-auto">
                            {{ selectSignatureTypeLabel }}
                        </div>
                        <div class="col-auto">
                            <pc-button
                                class="btn-sm"
                                style="min-width: 0"
                                v-bind:disabled="readonly"
                                v-on:click="setOptionalToRequired">
                                {{ chooseESignatureButtonLabel }}
                            </pc-button>
                        </div>
                        <div class="col-auto">
                            <pc-button
                                class="btn-sm"
                                style="min-width: 0"
                                v-bind:disabled="readonly"
                                v-on:click="setOptionalToNone">
                                {{ chooseSignAndUploadButtonLabel }}
                            </pc-button>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    key="other"
                    class="col-12">
                    <div class="row gx-3 align-items-start">
                        <div v-bind:class="responsiveHelpers.long">
                            <div class="row gx-3 align-items-start">
                                <div
                                    v-if="showTemplate"
                                    class="col-auto mb-2 mb-lg-0 align-self-center">
                                    <span>
                                        {{ templateName }}
                                    </span>
                                    <span
                                        v-if="eSignatureIsRequested && !hasCompletedESignature"
                                        class="ms-2 text-bubble-info">
                                        {{ outForSigningMessage }}
                                    </span>
                                </div>
                                <div
                                    v-if="showFileName"
                                    class="col-auto">
                                    <div class="row gx-3">
                                        <div class="col-auto mb-2 mb-lg-0 align-self-center">
                                            {{ attachment.FileName }}
                                        </div>
                                        <div class="col-auto mb-2 mb-lg-0 align-self-center">
                                            <pc-loader
                                                v-bind:loading="downloading"
                                                small>
                                                <pc-button
                                                    class="btn-sm btn-secondary shadow-none"
                                                    style="min-width: 0"
                                                    v-on:click="downloadAttachment">
                                                    {{ viewButtonLabel }}
                                                </pc-button>
                                            </pc-loader>
                                        </div>
                                    </div>
                                    <div class="small">
                                        {{ receivedOn }}
                                    </div>
                                </div>
                                <div
                                    v-if="showPreview"
                                    class="col-auto mb-2 mb-lg-0">
                                    <pc-loader
                                        v-bind:loading="generatingPreview"
                                        small>
                                        <pc-button
                                            class="btn-sm"
                                            v-bind:disabled="readonly"
                                            v-on:click="preview">
                                            {{ previewDocumentButtonLabel }}
                                        </pc-button>
                                    </pc-loader>
                                </div>
                                <div
                                    v-if="showPcFile"
                                    class="col-auto flex-grow-1">
                                    <pc-loader
                                        v-bind:loading="removingDocument"
                                        small>
                                        <pc-file
                                            v-bind:id="inputId"
                                            v-model="document"
                                            v-bind:required="required"
                                            v-bind:readonly="readonly"
                                            v-bind:disabled="showEditFormPlaceholder"
                                            v-bind:upload-function="uploadFile"
                                            v-bind:extensions="allowedExtensions"
                                            v-bind:show-extensions="showExtensions"
                                            v-bind:max-allowed-size-kb="maxAllowedSizeKb"
                                            v-bind:custom-remove-file-message="customRemoveFileMessage"
                                            v-bind:show-view="showViewBtn"
                                            v-bind:view-function="downloadAttachment.bind(this)"
                                            v-bind:class="documentInputClass"
                                            v-on:uploading-changed="uploadingChanged($event)"
                                            v-on:upload-canceled="uploadCanceled" />
                                    </pc-loader>
                                    <div class="small">
                                        {{ uploadedBy }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="showAttachmentNote"
                            v-bind:class="responsiveHelpers.standard">
                            <div>{{ attachmentStatusNoteText }}</div>
                            <!-- eslint-disable-next-line -->
                            <div style="white-space: pre-wrap">{{ attachmentStatusNote }}</div>
                        </div>
                        <div
                            v-if="showAttachmentStatus"
                            v-bind:class="responsiveHelpers.short">
                            <div>
                                {{ attachmentStatusLabel }}
                            </div>
                            <span v-bind:class="attachmentStatusClass">{{ attachmentStatus }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
    import { PROJECT_FORM_TYPES } from "../ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "../ProjectForm/projectFormControllerStore";
    import { E_SIGNATURE_OPTION } from "../ProjectForm/ProjectElements/projectElement";
    import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import { EDIT_FORM_NAMESPACE } from "Forms/EditForm/editFormStoreDefinitions";
    import { FADE_IN_OUT } from "Util/animations";
    import {
        getApprovalStatusDisplay,
        getApprovalStatusDisplayClass,
        ATTACHMENT_APPROVAL_STATUS
    } from "Util/clientAttachment";
    import { downloadFile } from "Util/util";
    import { isModuleRegistered } from "SharedStores/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/attachment.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import base from "./_elementBase";
    import extractorWarningDialog from "./extractorWarningDialog.vue";
    import attachmentValidationResponse from "Projects/AttachmentView/attachmentValidationResponse.vue";

    export default {
        extends: base,
        inject: {
            viewport: {
                from: "viewport",
                default: null
            },
            anonParams: {
                from: "anonParams",
                default: null
            }
        },
        data() {
            return {
                generatingPreview: false,
                uploading: false,
                downloading: false,
                removingDocument: false
            };
        },
        computed: {
            controller() {
                return this.$serviceControllers.Attachments;
            },
            uploadedBy() {
                if (this.attachment) {
                    return this.uploadedByMessage;
                }
                return null;
            },
            receivedOn() {
                if (this.attachment) {
                    return this.receivedOnMessage;
                }
                return null;
            },
            isOptionalChoiceMade() {
                return (
                    this.element.ESignatureConstant === E_SIGNATURE_OPTION.Optional &&
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.SignatureChoice !== E_SIGNATURE_OPTION.Optional
                );
            },
            signatureChoiceIsElectronic() {
                return (
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.SignatureChoice === E_SIGNATURE_OPTION.Required
                );
            },
            signatureChoiceIsSignAndUpload() {
                return (
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.SignatureChoice === E_SIGNATURE_OPTION.None
                );
            },
            isSignAndUpload() {
                if (!this.element.ESignatureConstant || this.element.ESignatureConstant === E_SIGNATURE_OPTION.None) {
                    return true;
                }
                if (this.isOptionalChoiceMade && this.signatureChoiceIsSignAndUpload) {
                    return true;
                }
                return false;
            },
            isESignature() {
                if (this.isOptionalChoiceMade && this.signatureChoiceIsElectronic) {
                    return true;
                }
                if (
                    this.element.ESignatureConstant &&
                    this.element.ESignatureConstant === E_SIGNATURE_OPTION.Required
                ) {
                    return true;
                }
                return false;
            },
            templateName() {
                if (this.element.AuxiliaryModel) {
                    return this.element.AuxiliaryModel.TemplateName;
                }
                return null;
            },
            showExtensions() {
                return this.getBooleanPropertyValue("showallowedextensions");
            },
            showAsk() {
                return (
                    this.element.ESignatureConstant === E_SIGNATURE_OPTION.Optional &&
                    ((this.projectElement &&
                        this.projectElement.AuxiliaryModel &&
                        this.projectElement.AuxiliaryModel.SignatureChoice === E_SIGNATURE_OPTION.Optional) ||
                        this.showEditFormPlaceholder)
                );
            },
            showTemplate() {
                if (this.showPcFile || this.hasCompletedESignature) {
                    return false;
                }
                const choseRequired =
                    this.element.ESignatureConstant === E_SIGNATURE_OPTION.Optional &&
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.SignatureChoice === E_SIGNATURE_OPTION.Required;
                return this.element.ESignatureConstant === E_SIGNATURE_OPTION.Required || choseRequired;
            },
            showError() {
                return this.showTemplateError || this.showSignatoryError;
            },
            showTemplateError() {
                return (
                    this.element.ESignatureConstant &&
                    (!this.element.AuxiliaryModel || !this.element.AuxiliaryModel.TemplateName)
                );
            },
            showSignatoryError() {
                return (
                    this.element.AuxiliaryModel &&
                    this.element.AuxiliaryModel.TemplateName &&
                    this.element.ESignatureConstant &&
                    (this.element.ESignatureConstant === E_SIGNATURE_OPTION.Optional ||
                        this.element.ESignatureConstant === E_SIGNATURE_OPTION.Required) &&
                    (!this.element.Signatories || this.element.Signatories.length <= 0)
                );
            },
            error() {
                if (this.showTemplateError) {
                    return this.templateErrorMessage;
                }
                if (this.showSignatoryError) {
                    return this.showSignatoryErrorMessage;
                }
                return null;
            },
            showPcFile() {
                if (this.attachmentIsPartOfV2Envelope) {
                    return false;
                }
                if (this.element.ESignatureConstant === E_SIGNATURE_OPTION.Required) {
                    return false;
                }
                if (this.element.ESignatureConstant === E_SIGNATURE_OPTION.Optional) {
                    return (
                        this.projectElement &&
                        this.projectElement.AuxiliaryModel &&
                        this.projectElement.AuxiliaryModel.SignatureChoice === E_SIGNATURE_OPTION.None
                    );
                }
                return true;
            },
            showFileName() {
                return !this.showPcFile && this.attachment && this.hasCompletedESignature;
            },
            showPreview() {
                if (
                    this.uploading ||
                    !this.templateName ||
                    (this.isESignature && this.eSignatureIsRequested) ||
                    (this.isSignAndUpload && this.attachment)
                ) {
                    return false;
                }

                return this.element.ESignatureConstant !== E_SIGNATURE_OPTION.Optional || this.isOptionalChoiceMade;
            },
            showChange() {
                return (
                    !this.uploading &&
                    this.isOptionalChoiceMade &&
                    ((this.isSignAndUpload && !this.attachment) ||
                        (this.isESignature && !this.eSignatureIsRequested)) &&
                    !this.hasCompletedESignature
                );
            },
            showAttachmentNote() {
                if (this.signatureChoiceIsElectronic && !this.hasCompletedESignature) {
                    return false;
                }
                return this.showAttachmentStatus || this.attachmentStatusNote;
            },
            showAttachmentStatus() {
                if (this.signatureChoiceIsElectronic && !this.hasCompletedESignature) {
                    return false;
                }
                return (
                    this.attachment &&
                    !this.attachment.IsHidden &&
                    (this.attachment.Status === ATTACHMENT_APPROVAL_STATUS.approved ||
                        this.attachment.Status === ATTACHMENT_APPROVAL_STATUS.rejected ||
                        this.attachmentStatusNote)
                );
            },
            attachment() {
                if (this.isProjectModuleRegistered()) {
                    return this.$store.getters[PROJECT_TYPES.getters.attachmentFromId](this.element.AttachmentId);
                }
                return null;
            },
            attachmentDefinition() {
                return this.$store.getters[PROGRAM_TYPES.getters.attachmentDefinitionFromId](this.element.AttachmentId);
            },
            attachmentIsApproved() {
                let approved = false;
                if (this.attachment) {
                    approved = this.attachment.Status === ATTACHMENT_APPROVAL_STATUS.approved;
                }
                return approved;
            },
            attachmentIsRejected() {
                let rejected = false;
                if (this.attachment) {
                    rejected = this.attachment.Status === ATTACHMENT_APPROVAL_STATUS.rejected;
                }
                return rejected;
            },
            attachmentStatus() {
                return getApprovalStatusDisplay(this.attachment.Status, true);
            },
            attachmentStatusClass() {
                return getApprovalStatusDisplayClass(this.attachment.Status, true);
            },
            attachmentStatusNote() {
                let note = "";
                if (this.attachment && this.attachment.Note) {
                    note = this.attachment.Note.trim();
                }
                return note;
            },
            eSignatureIsRequested() {
                let isRequested = false;
                if (
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.SignatureRequested
                ) {
                    isRequested = true;
                }
                return isRequested;
            },
            attachmentIsPartOfV2Envelope() {
                if (this.attachment) {
                    return this.$store.getters[PROJECT_TYPES.getters.isAttachmentIdPartOfEnvelope](
                        this.attachment.AttachmentId
                    );
                }
                return false;
            },
            hasCompletedESignature() {
                if (this.attachmentIsPartOfV2Envelope) {
                    return true;
                }
                if (
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.HasCompletedESignature
                ) {
                    return true;
                }
                return false;
            },
            allowedExtensions() {
                return this.attachmentDefinition ? this.attachmentDefinition.AllowedExtensions : [];
            },
            maxAllowedSizeKb() {
                return this.attachmentDefinition ? this.attachmentDefinition.MaxAllowedSizeKb : null;
            },
            isSensitive() {
                return this.attachmentDefinition ? this.attachmentDefinition.IsSensitive : false;
            },
            showViewBtn() {
                return Boolean(
                    this.attachment && (!this.isSensitive || this.$app.loggedInUserHasViewSensitiveDataPrivilege)
                );
            },
            document: {
                get() {
                    if (this.attachment) {
                        return { DocumentId: this.attachment.DocumentId, FileName: this.attachment.FileName };
                    }
                    return null;
                },
                async set(value) {
                    /**
                     * we only need to listen to clear events, no need to
                     * bother with actually setting otherwise since we are overriding
                     * the default upload method of the pc-file component,
                     * and that method will set the value in the store
                     */
                    if (!value && this.document) {
                        try {
                            this.removingDocument = true;
                            await this.removeAttachment();
                        } finally {
                            this.removingDocument = false;
                        }
                    }
                }
            },
            documentInputClass() {
                return { "edit-form-document-override": this.showEditFormPlaceholder && !this.readonly };
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            showEditFormPlaceholder() {
                return isModuleRegistered(EDIT_FORM_NAMESPACE, this.$store.state);
            },
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            ...mapI18n(
                {
                    ...KEYS,
                    uploadedByMessage() {
                        return {
                            key: KEYS.uploadedByMessageText,
                            values: {
                                firstName: this.attachment.UploadUser.FirstName,
                                lastName: this.attachment.UploadUser.LastName,
                                date: this.attachment.UploadTimestamp.Display
                            }
                        };
                    },
                    receivedOnMessage() {
                        return {
                            key: KEYS.receivedOnMessageText,
                            values: {
                                date: this.attachment.UploadTimestamp.Display
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        methods: {
            async preview() {
                if (!this.isProjectFormModuleRegistered()) {
                    return;
                }
                try {
                    this.generatingPreview = true;
                    await this.$store.dispatch(
                        PROJECT_FORM_TYPES.actions.generateSignatureDocument,
                        {
                            elementId: this.element.PublicId
                        },
                        { root: true }
                    );
                } finally {
                    this.generatingPreview = false;
                }
            },
            changeOptionalChoice() {
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setSignatureChoice,
                    {
                        elementId: this.element.PublicId,
                        signatureChoice: E_SIGNATURE_OPTION.Optional
                    },
                    { root: true }
                );
            },
            setOptionalToRequired() {
                if (!this.isProjectFormModuleRegistered()) {
                    return;
                }
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setSignatureChoice,
                    {
                        elementId: this.element.PublicId,
                        signatureChoice: E_SIGNATURE_OPTION.Required
                    },
                    { root: true }
                );
            },
            setOptionalToNone() {
                if (!this.isProjectFormModuleRegistered()) {
                    return;
                }
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setSignatureChoice,
                    {
                        elementId: this.element.PublicId,
                        signatureChoice: E_SIGNATURE_OPTION.None
                    },
                    { root: true }
                );
            },
            async downloadAttachment() {
                if (this.isSensitive && !(await this._confirmDownloadSensitiveAttachment())) {
                    return;
                }

                this.downloading = true;
                try {
                    const result = await this.controller.ViewAttachment2({
                        programId: this.controller.ProgramId,
                        attachmentId: this.attachment.AttachmentId
                    });
                    downloadFile(result.Data.Url, this.anonParams);
                } finally {
                    this.downloading = false;
                }
            },
            async _confirmDownloadSensitiveAttachment() {
                // If attachment sensitive and user doesn't have MFA enabled (and is
                // not an SSO user) prompt them to enable MFA before allowing download.
                if (!this.$app.loggedInUserHasMfa && !this.$app.loggedInUserIsSsoInProgram) {
                    this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.showMfaDialog, {}, { root: true });
                    return false;
                }
                const okResult = "OK";
                const cancelResult = "CANCEL";
                const dialogResult = await this.$app.showDialog({
                    title: "Download Sensitive Attachment",
                    message: "Are you sure you want to download this sensitive attachment?",
                    buttons: [
                        {
                            text: "Cancel",
                            result: cancelResult
                        },
                        {
                            text: "OK",
                            primary: true,
                            result: okResult
                        }
                    ]
                });
                return dialogResult === okResult;
            },
            async removeAttachment() {
                return this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.removeAttachment,
                    {
                        attachmentDefinitionId: this.element.AttachmentId
                    },
                    { root: true }
                );
            },
            uploadingChanged(value) {
                this.uploading = value;
            },
            uploadCanceled() {
                if (this.xhr) {
                    this.uploading = false;
                    this.xhr.abort();
                }
            },
            async uploadFile(file) {
                if (this.isProjectFormControllerModuleRegistered()) {
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_TYPES.actions.uploadAttachment,
                        {
                            file,
                            formElementId: this.element.PublicId,
                            attachmentDefinitionId: this.element.AttachmentId,
                            captureXhr: this.captureXhr
                        },
                        { root: true }
                    );

                    const failureFields = result.extractorFailureFields;
                    if (failureFields.length) {
                        this.$app.showDialog({
                            title: this.extractorWarningDialogTitle,
                            customComponent: extractorWarningDialog,
                            customComponentOptions: {
                                extractorFailureFields: failureFields
                            },
                            buttons: [
                                {
                                    text: this.okText,
                                    primary: true
                                }
                            ]
                        });
                    }

                    const attachmentValidationQuestion = "Does the test image match the reference images?";
                    if (result.validationResponse) {
                        if (result.validationResponse.Detail) {
                            this.$app.showDialog({
                                title: attachmentValidationQuestion,
                                customComponent: attachmentValidationResponse,
                                customComponentOptions: {
                                    validationResponseDetail: result.validationResponse.Detail
                                },
                                buttons: [
                                    {
                                        text: this.okText,
                                        primary: true
                                    }
                                ]
                            });
                        } else {
                            this.$app.showDialog({
                                title: attachmentValidationQuestion,
                                message: result.validationResponse.ResponseText,
                                buttons: [
                                    {
                                        text: this.okText,
                                        primary: true
                                    }
                                ]
                            });
                        }
                    }

                    return result.attachment;
                }
                return null;
            },
            captureXhr(xhr) {
                this.xhr = xhr;
            },
            enter: FADE_IN_OUT.enter,
            leave: FADE_IN_OUT.leave
        }
    };
</script>

<style lang="scss" scoped>
    // override to make the pc-file appear editable in form editor despite being disabled
    // :deep so it propagates down to the custom file input despite being scoped
    .edit-form-document-override:deep(.custom-file-input:disabled ~ .custom-file-label) {
        background-color: var(--bs-white);
    }
</style>
